import { isValidLayoutName } from "./basefile.utils";

function normalizeLayoutSegment(layout: string | null | undefined): string {
    const trimmed = (layout || "").trim();
    if (!trimmed) return "";
    if (!isValidLayoutName(trimmed)) return "";
    return `~${trimmed}`;
}

function getLayoutFromAnchorAttributes(attributes: string): string | undefined {
    const match = attributes.match(/(?:^|\s)layout\s*=\s*(?:(["'])(.*?)\1|([^\s"'`>]+))/i);
    if (!match) return undefined;
    return (match[2] ?? match[3] ?? "").trim();
}

function removeLayoutAttribute(attributes: string): string {
    return attributes.replace(/(?:^|\s+)layout\s*=\s*(?:(["']).*?\1|[^\s"'`>]+)/gi, "");
}

function getCurrentPathWithoutLayout(currentPath: string | undefined, sitePath: string): string {
    const path = (currentPath || "").split("?")[0];
    const withoutSitePath = sitePath && path.startsWith(sitePath)
        ? path.substring(sitePath.length)
        : path;
    const segments = withoutSitePath.split("/").filter(Boolean);
    if (segments[0]?.startsWith("~")) {
        segments.shift();
    }
    return segments.length ? segments.join("/") : "";
}

export function fixInternalLinks(text: string, sitePath: string, currentLayout?: string, currentPath?: string): string {
    const currentPathWithoutLayout = getCurrentPathWithoutLayout(currentPath, sitePath);
    // Find all <a> tags with href starting with /, supporting both single and double quotes
    const regex = /<a\s+([^>]*?)href=(["'])\/([^"']*)\2([^>]*)>/gis;
    const withHrefFixed = text.replace(regex, (match, beforeHref, quote, hrefPath, afterHref) => {
        const allAttributes = `${beforeHref}${afterHref}`;
        const rawLayout = getLayoutFromAnchorAttributes(allAttributes);
        const layoutSegment = rawLayout === undefined
            ? normalizeLayoutSegment(currentLayout)
            : normalizeLayoutSegment(rawLayout);
        const cleanedBeforeHref = removeLayoutAttribute(beforeHref);
        const cleanedAfterHref = removeLayoutAttribute(afterHref);
        // Reconstruct the <a> tag with sitePath prepended to the href
        const pathWithLayout = layoutSegment
            ? `${sitePath}/${layoutSegment}/${hrefPath}`
            : `${sitePath}/${hrefPath}`;
        return `<a ${cleanedBeforeHref}href=${quote}${pathWithLayout}${quote}${cleanedAfterHref}>`;
    });

    // Support anchors that omit href but specify layout: keep the current in-site path and only alter layout segment.
    const missingHrefRegex = /<a\s+([^>]*?)>/gis;
    return withHrefFixed.replace(missingHrefRegex, (match, attributes) => {
        if (/\shref\s*=/i.test(attributes)) {
            return match;
        }
        const rawLayout = getLayoutFromAnchorAttributes(attributes);
        if (rawLayout === undefined) {
            return match;
        }
        const layoutSegment = normalizeLayoutSegment(rawLayout);
        const cleanedAttributes = removeLayoutAttribute(attributes);
        const pathWithLayout = layoutSegment
            ? `${sitePath}/${layoutSegment}/${currentPathWithoutLayout}`
            : `${sitePath}/${currentPathWithoutLayout}`;
        return `<a ${cleanedAttributes}href='/${pathWithLayout.replace(/^\/+/, "")}'>`;
    });
}

function rewriteLeadingSlashFileAttribute(
    text: string,
    sitePath: string,
    tagPattern: RegExp,
    attributeName: string,
): string {
    return text.replace(tagPattern, (_match, tagName, before, quote, quotedPath, unquotedPath, after) => {
        const attrPath = quotedPath || unquotedPath;
        const attrValue = quote
            ? `${quote}${sitePath}/${attrPath}${quote}`
            : `${sitePath}/${attrPath}`;
        return `<${tagName} ${before}${attributeName}=${attrValue}${after}>`;
    });
}

export function fixInternalImages(text: string, sitePath: string): string {
    // Rewrite src on media tags, then video poster, when the value is an internal Topic Finder path (leading /).
    const withSrc = rewriteLeadingSlashFileAttribute(
        text,
        sitePath,
        /<(img|image|video|model-viewer)\s+([^>]*?)src\s*=\s*(?:(["'])\/([^"']*)\3|\/([^\s"'`>]+))([^>]*)>/gis,
        "src",
    );
    return rewriteLeadingSlashFileAttribute(
        withSrc,
        sitePath,
        /<(video)\s+([^>]*?)poster\s*=\s*(?:(["'])\/([^"']*)\3|\/([^\s"'`>]+))([^>]*)>/gis,
        "poster",
    );
}
